import { Howl } from 'howler';
import { AudioPlayer } from './audio';
import { Scenes } from './scenes';
import { WaterFaucetScene } from './water-faucet-scene';

/**
 * Loads the initial elements that are needed for the experiece to run
 */
export class Loading {
	protected justWaterLoadingElement = document.querySelector('#jw-loading');
	protected startButtonElement = document.querySelector('#start-button');
	protected experienceLoading = document.querySelector('.experience-loading');

	private audioPlayer = AudioPlayer.getInstance();
	private waterFaucetScene = WaterFaucetScene.getInstance();
	private scenes = new Scenes();

	private audioLoadCount = 0;
	private imageLoadCount = 0;

	private isIos = false;

	/**
	 * If the device is IOS we do a different loading method
	 */
	public iosLoad(): void {
		this.isIos = true;
		this.justWaterLoadingElement.style.display = 'none';
		this.startButtonElement.style.display = 'block';
		this.listenForStartClick();
	}

	/**
	 * This loads all the audio within the experience
	 */
	public loadAudio(): void {
		const voiceOvers = this.audioPlayer.voiceOvers;
		voiceOvers.opening.load();
		voiceOvers.waterFaucet.load();
		voiceOvers.lightSwitch.load();
		voiceOvers.transport.load();
		voiceOvers.final.load();

		const soundFx = this.audioPlayer.soundFx;

		const general = soundFx.general;
		general.actionChime.load();
		general.correctActionChime.load();
		general.wrongActionChime.load();

		const waterFaucetScene = soundFx.waterFaucetScene;
		waterFaucetScene.runningWater.load();
		waterFaucetScene.waterDraining.load();
		waterFaucetScene.underwaterAmbience.load();
		waterFaucetScene.waterSplashing.load();
		waterFaucetScene.faucetSqueak.load();

		const lightSwitchScene = soundFx.lightSwitchScene;
		lightSwitchScene.energyDrone.load();
		lightSwitchScene.lightSwitchOff.load();
		lightSwitchScene.doorOpening.load();

		const transportScene = soundFx.transportScene;
		transportScene.biycleNoise.load();
		transportScene.bikePedals.load();
		transportScene.birdsChirping.load();
		transportScene.carIdling.load();
		transportScene.carRollout.load();
		transportScene.sheep.load();

		const finalScene = soundFx.finalScene;
		finalScene.brightDrone.load();
		finalScene.forrestSounds.load();

		// If the device is ios we dont need to listen for load, because it wont actually load anyway
		if (!this.isIos) {
			this.listenForLoadedAudio(this.audioPlayer.voiceOvers);
			this.listenForLoadedAudio(general);
			this.listenForLoadedAudio(waterFaucetScene);
			this.listenForLoadedAudio(lightSwitchScene);
			this.listenForLoadedAudio(transportScene);
			this.listenForLoadedAudio(finalScene);
		}
	}

	/**
	 * This loads all the initially seen images in the water-faucet scene
	 */
	private loadImages(): void {
		const sceneElements = this.waterFaucetScene.sceneElements;

		const primaryWaves = sceneElements.primaryWaves;
		primaryWaves.one.src = './static/images/water-one.png';
		primaryWaves.two.src = './static/images/water-two.png';
		primaryWaves.three.src = './static/images/water-three.png';
		primaryWaves.four.src = './static/images/water-four.png';
		primaryWaves.five.src = './static/images/water-five.png';
		this.listenForLoadedImage(primaryWaves);

		const secondaryWaves = sceneElements.secondaryWaves;
		secondaryWaves.one.src = './static/images/secondary-water-one.png';
		secondaryWaves.two.src = './static/images/secondary-water-two.png';
		secondaryWaves.three.src = './static/images/secondary-water-three.png';
		this.listenForLoadedImage(secondaryWaves);

		const primaryFishTwo = sceneElements.fish.primary.fishTwo;
		Object.keys(primaryFishTwo).forEach((key) => {
			(primaryFishTwo as any)[key].src = './static/images/fish-two.png';
		});
		this.listenForLoadedImage(primaryFishTwo);

		const primaryFishThree = sceneElements.fish.primary.fishThree;
		Object.keys(primaryFishThree).forEach((key) => {
			(primaryFishThree as any)[key].src = './static/images/fish-three.png';
		});
		this.listenForLoadedImage(primaryFishThree);

		const primaryFishFour = sceneElements.fish.primary.fishFour;
		primaryFishFour.src = './static/images/fish-four.png';
		this.listenForLoadedImage(primaryFishFour);

		const secondaryFish = sceneElements.fish.secondary;
		Object.keys(secondaryFish).forEach((key) => {
			(secondaryFish as any)[key].src = './static/images/fish-one.png';
		});
		this.listenForLoadedImage(secondaryFish);

		const coffeeMug = sceneElements.coffeeMug;
		coffeeMug.mug.src = './static/images/mug.png';
		coffeeMug.toothbrush.src = './static/images/toothbrush.png';
		this.listenForLoadedImage(coffeeMug);

		const tap = sceneElements.tapHandle;
		tap.base.src = './static/images/tap-handle.png';
		tap.glow.src = './static/images/tap-handle-glow.png';
		this.listenForLoadedImage(tap);

		sceneElements.bathroomSink.src = './static/images/sink-with-tap.png';
		this.listenForLoadedImage(sceneElements.bathroomSink);

		sceneElements.towels.src = './static/images/towels.png';
		this.listenForLoadedImage(sceneElements.towels);

		sceneElements.bathroomBackground.src = './static/images/bathroom-background.jpg';
		this.listenForLoadedImage(sceneElements.bathroomBackground);

		sceneElements.runningWaterPreload.src = './static/images/water-running-spritesheet.png';
		this.listenForLoadedImage(sceneElements.runningWaterPreload);
	}

	/**
	 * This sets an event listener for 'canplaythrough' on each item in the provided object
	 * This way we know when all the audio has loaded so we can play the experience with no lag
	 */
	private listenForLoadedAudio(audioRefObject: { [key: string]: Howl }): void {
		Object.keys(audioRefObject).forEach((key) => {
			((audioRefObject as any)[key] as Howl).on('load', this.updateAudioLoadCount.bind(this));
		});
	}

	/**
	 * For each audio file that has loaded at 1 to the audioLoadCount
	 */
	private updateAudioLoadCount(): void {
		this.audioLoadCount += 1;

		// If the device is not IOS load the images
		if (this.audioLoadCount === 24 && !this.isIos) {
			this.loadImages();
		}
	}

	/**
	 * This sets an event listener for 'load' on each item in the provided object
	 * This way we know when all the images have loaded so we can play the experience with no lag
	 */
	private listenForLoadedImage(imageRef: { [key: string]: HTMLImageElement | HTMLElement } | HTMLImageElement): void {
		const keys = Object.keys(imageRef);
		if (keys.length > 0) {
			keys.forEach((key) => {
				(imageRef as any)[key].addEventListener('load', this.updateImageLoadCount.bind(this), { once: true });
			});
		} else {
			(imageRef as HTMLImageElement).addEventListener('load', this.updateImageLoadCount.bind(this), { once: true });
		}
	}

	/**
	 * For each image that has loaded add 1 to the imageLoadCount
	 */
	private updateImageLoadCount(): void {
		this.imageLoadCount += 1;
		if (this.imageLoadCount === 28 && !this.isIos) {
			this.justWaterLoadingElement.style.display = 'none';
			this.startButtonElement.style.display = 'block';
			this.listenForStartClick();
		}
	}

	private listenForStartClick(): void {
		this.startButtonElement.addEventListener('click', () => {
			// If the device is IOS we need to do some stuff to make the experience load properly
			if (this.isIos) {
				// DO NOT REMOVE
				// This is a small hack to make sure that the audio plays correctly when the phone is on silent
				// Working as of iOS 14.6
				new Audio('../static/audio/silence.mp3').play();

				this.loadAudio();
				this.loadImages();
				this.justWaterLoadingElement.style.display = 'block';
				this.startButtonElement.style.display = 'none';
				const voiceOvers = this.audioPlayer.voiceOvers;
				const soundFx = this.audioPlayer.soundFx;

				const imageInterval = setInterval(() => {
					console.log(this.imageLoadCount);
					if (this.imageLoadCount === 28) {
						clearInterval(imageInterval);

						this.triggerAudio(voiceOvers.opening);
						this.triggerAudio(voiceOvers.waterFaucet);
						this.triggerAudio(voiceOvers.lightSwitch);
						this.triggerAudio(voiceOvers.transport);
						this.triggerAudio(voiceOvers.final);

						const waterFaucetScene = soundFx.waterFaucetScene;
						this.triggerAudio(waterFaucetScene.runningWater);
						this.triggerAudio(waterFaucetScene.waterDraining);
						this.triggerAudio(waterFaucetScene.underwaterAmbience);
						this.triggerAudio(waterFaucetScene.waterSplashing);
						this.triggerAudio(waterFaucetScene.faucetSqueak);

						const general = soundFx.general;
						this.triggerAudio(general.actionChime);
						this.triggerAudio(general.correctActionChime);
						this.triggerAudio(general.wrongActionChime);

						const lightSwitchScene = soundFx.lightSwitchScene;
						this.triggerAudio(lightSwitchScene.energyDrone);
						this.triggerAudio(lightSwitchScene.lightSwitchOff);
						this.triggerAudio(lightSwitchScene.doorOpening);

						const transportScene = soundFx.transportScene;
						this.triggerAudio(transportScene.biycleNoise);
						this.triggerAudio(transportScene.bikePedals);
						this.triggerAudio(transportScene.birdsChirping);
						this.triggerAudio(transportScene.carIdling);
						this.triggerAudio(transportScene.carRollout);
						this.triggerAudio(transportScene.sheep);

						const finalScene = soundFx.finalScene;
						this.triggerAudio(finalScene.brightDrone);
						this.triggerAudio(finalScene.forrestSounds);

						this.startExperience();
					}
				}, 1000);
				// If the device is android we dont need to do anything else we can start the experience
			} else {
				this.startExperience();
			}
		});
	}

	private startExperience(): void {
		this.scenes.start();
		this.experienceLoading.classList.add('fade-out');
		setTimeout(() => {
			this.experienceLoading.remove();
			this.startButtonElement.remove();
		}, 2000);
	}

	private triggerAudio(audioElement: Howl): void {
		audioElement.mute(true);
		audioElement.play();
		audioElement.stop();
		audioElement.mute(false);
	}
}
